import * as React from 'react';
import 'react-table/react-table.css';
import { Plus, X, Download } from 'react-feather';
import { debounce } from 'lodash';
import ReactTable, { RowInfo, ComponentDecoratorProps } from 'react-table';
import styled from 'styled-components';
import { Box, Button, Loader, NoDataHandler, StyledInput } from '.';
import { H3 } from '../typography';
import { theme } from '../theme';
import Select from 'react-select';
import { themeGet } from 'styled-system';
import { SelectObject } from '../utils/types';
import { unwrap, downloadCSV, downloadXLSX } from '../utils/helpers';

export type TableProps<D = any, ResolvedData = D> = {
  data?: D[] | D;
  columns?: any[];
  subColumns?: any[];
  exportColumns?: any[];
  customFilterRow?: React.ReactNode;
  heading?: string;
  loading?: boolean;
  buttonText?: string;
  subTableComponent?: any;
  hasAction?: boolean;
  defaultPageSize?: number;
  onRowClick?: (rowInfo: RowInfo) => void;
  onButtonPress?: () => void;
  resolveData?: (data: D[]) => ResolvedData[];
  getTrProps?: (trprops: ComponentDecoratorProps, rowInfo: RowInfo) => void;
  onSearchCallback?: (text: string) => void;
  filterOptions?: SelectObject[] | { label: string; options: SelectObject[] }[];
  filter?: boolean;
  onFilterCallback?: (value: any) => void;
  onClearFilterCallback?: () => void;
  showPageSizeOptions?: boolean;
  disabled?: boolean;
  pivotBy?: string[];
  disableSearch?: boolean;
  resizable?: boolean;
  minWidth?: string;
  defaultSorted?: any[];
};

type TableState = { value?: any };

// TODO: Seperate this StyledSelect filter component into it's own. This will modularize and help with testing!
const StyledSelect = styled(Select)`
  & .react-select__control {
    min-height: 40px;
    width: 220px;
    margin-left: 20px;
  }
  & .react-select__menu .react-select__menu-list .react-select__option--is-selected {
    background-color: ${themeGet('colors.putty', '#EEE')};
  }
  & .react-select__control--is-focused {
    border: solid 2px ${themeGet('colors.putty', '#EEE')};
    box-shadow: none;
  }
  & .react-select__control:hover {
    border: solid 2px ${themeGet('colors.putty', '#EEE')};
    box-shadow: none;
  }
`;

const StyledTable = styled(ReactTable)<TableProps>`
  flex: 1;
  background: white;
  border-radius: 4px;
  min-width: ${(props) => props.minWidth || 'auto'} !important;
  /* TODO: try get rid of important tags here */
  & .rt-thead.-header {
    box-shadow: 0 0px 4px 0px rgba(50, 50, 93, 0.3) !important;
  }
  & .rt-thead .rt-th {
    padding: 15px !important;
  }
  & .rt-td {
    justify-content: center;
    align-items: center;
    display: flex;
    white-space: normal !important;
    text-align: center !important;
    word-break: break-all;
  }
  & .rt-tr-group:hover {
    cursor: pointer;
  }
`;

const StyledSubTable = styled(ReactTable)<TableProps>`
  flex: 1;
  background: ${theme.colors.paleGrey};
  border-radius: 4px;
  min-width: ${(props) => props.minWidth || 'auto'} !important;
  /* TODO: try get rid of important tags here */
  & .rt-thead.-header {
    box-shadow: 0 0px 4px 0px rgba(50, 50, 93, 0.3) !important;
  }
  & .rt-thead .rt-th {
    padding: 15px !important;
  }
  & .rt-td {
    justify-content: center;
    align-items: center;
    display: flex;
    white-space: normal !important;
    text-align: start !important;
    word-break: break-all;
  }
  & .rt-tr-group:hover {
    cursor: pointer;
  }
`;

const NoDataWrapper = styled(Box)`
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0px 7px -1px rgba(50, 50, 93, 0.3);
`;

const NoData = () => (
  <NoDataWrapper p={4} bg="white" color="coal" borderRadius={4} position="absolute">
    <H3 fontWeight={300}>No Rows Found</H3>
  </NoDataWrapper>
);

const ActionsWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledX = styled(X)<any>`
  width: 20px;
  height: 20px;
  margin-left: 10px;
  color: ${themeGet('colors.putty', '#EEE')};
  position: relative;
  &:hover {
    cursor: pointer;
  }
`;

export type RowClickData = {
  _original: { id: string };
};

class ReactTableWithSubComponent extends React.Component<TableProps, TableState> {
  static defaultProps = {
    data: [],
    columns: [],
    defaultPageSize: 10,
    heading: 'New Table',
    buttonText: 'Add New',
    defaultSorted: [],
  };

  // prettier-ignore
  debounceCallback = debounce((text: string) => {
    const { onSearchCallback } = this.props;
    if (onSearchCallback) {
      onSearchCallback(text);
    }
  }, 1000);

  constructor(props: TableProps) {
    super(props);
    this.state = {
      value: undefined,
    };
  }

  handleRowClick(rowInfo: RowInfo) {
    const { onRowClick } = this.props;
    if (onRowClick) {
      onRowClick(rowInfo);
    }
    return null;
  }

  handleChange = (e: React.SyntheticEvent): void => {
    const target = e.target as HTMLInputElement;
    const input = target.value;
    this.debounceCallback(input);
  };

  handleFilter = (selectedValue: SelectObject): void => {
    const { onFilterCallback } = this.props;
    if (onFilterCallback) {
      this.setState({ value: selectedValue });
      onFilterCallback(selectedValue.value);
    }
  };

  clearFilter = () => {
    const { onClearFilterCallback } = this.props;
    if (onClearFilterCallback) {
      onClearFilterCallback();
      this.setState({ value: '' });
    }
  };

  export = (xlsx = false) => {
    const { data, exportColumns, heading } = this.props;
    const headings = (exportColumns || []).map((column) => column.heading);
    const headingAccessors = (exportColumns || []).map((column) => column.accessor);
    const mappedData = [
      headings,
      ...(data || []).map((item: any) =>
        headingAccessors.reduce((arr, currKey) => {
          let value = unwrap(item, currKey.split('.'));
          if (value instanceof Array) {
            value = `"${value.join(',')}"`;
          }
          return [...arr, value];
        }, [])
      ),
    ];
    if (xlsx) {
      return downloadXLSX(mappedData, `${(heading || 'export').replace(' ', '')}`);
    }
    return downloadCSV(mappedData, `${(heading || 'export').replace(' ', '')}`);
  };

  render() {
    const { value } = this.state;
    const {
      data,
      columns,
      heading,
      exportColumns,
      onButtonPress,
      buttonText,
      defaultPageSize,
      loading,
      resolveData,
      filterOptions,
      filter,
      showPageSizeOptions,
      disabled,
      pivotBy,
      disableSearch,
      resizable,
      minWidth,
      defaultSorted,
      subColumns,
      customFilterRow,
    } = this.props;
    return (
      <Box p={4} width="100%" flex={1}>
        <Box pb={4} width="100%" flexDirection="row" alignItems="center" justifyContent="space-between">
          <H3 fontWeight={300}>{heading}</H3>
          <ActionsWrap>
            {loading && <Loader color={theme.colors.putty} />}
            {customFilterRow}
            {filter && (
              <Box flexDirection="row" alignItems="center">
                <StyledSelect
                  classNamePrefix="react-select"
                  options={filterOptions}
                  onChange={this.handleFilter}
                  placeholder="Filter..."
                  value={value}
                />
                {value && <StyledX size={20} onClick={this.clearFilter} />}
              </Box>
            )}
            {!disableSearch && (
              <StyledInput
                ml={4}
                width="220px"
                bg="white"
                color="coal"
                placeholder="Search..."
                onChange={this.handleChange}
              />
            )}
            {onButtonPress && (
              <Button
                bg="coal"
                type="button"
                ml={4}
                color="white"
                width="auto"
                maxWidth="220px"
                text={buttonText}
                onClick={onButtonPress}
                disabled={disabled}
                icon={<Plus size={20} color="white" />}
              />
            )}
            {exportColumns && (
              <Button
                bg="coal"
                type="button"
                ml={4}
                color="white"
                width="auto"
                maxWidth="220px"
                text="Export"
                onClick={() => this.export()}
                icon={<Download size={20} color="white" />}
              />
            )}
          </ActionsWrap>
        </Box>
        {!loading && data && data.length === 0 && <NoDataHandler />}
        {!loading && data && data.length !== 0 && (
          <StyledTable
            getTrProps={(trprops: ComponentDecoratorProps, rowInfo: RowInfo) => {
              if (rowInfo) {
                return {
                  onClick: () => {
                    this.handleRowClick(rowInfo);
                  },
                };
              }
              return {};
            }}
            data={data}
            onExpandedChange={(e) => {
              console.log(e);
            }}
            columns={columns}
            NoDataComponent={NoData}
            resolveData={resolveData}
            className="-striped -highlight"
            defaultPageSize={defaultPageSize}
            showPageSizeOptions={showPageSizeOptions}
            pivotBy={pivotBy}
            resizable={resizable}
            minWidth={minWidth}
            // @ts-ignore
            SubComponent={(row) => {
              return (
                <StyledSubTable
                  columns={subColumns}
                  data={data}
                  NoDataComponent={NoData}
                  resolveData={resolveData}
                  defaultPageSize={4}
                  showPagination={false}
                  showPageSizeOptions={false}
                  defaultSorted={defaultSorted}
                />
              );
            }}
            defaultSorted={defaultSorted}
          />
        )}
      </Box>
    );
  }
}

export default ReactTableWithSubComponent;



// WEBPACK FOOTER //
// ./src/components/ReactTableWithSubComponent.tsx